// Shown to the host when someone is waiting in the knock queue. A labeled
// control, not a bare icon (a decision the host has to act on). Under the
// name, the door trivia they have answered while waiting, newest on top,
// each marked right or wrong (QUEUE row 423). On Let in the stack lifts
// away and the card folds up, while the guest is already on their way in.

import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import type { KnockAnswer, KnockEntry } from "@breeze/core";

interface KnockBannerProps {
  readonly knocks: readonly KnockEntry[];
  readonly onRespond: (knockId: string, allow: boolean) => void;
}

/** How many answers the card shows; older ones fold into the score. */
const SHOWN = 4;
/** How long a let-in card takes to animate out before it is dropped. */
const LEAVE_MS = 520;

export function KnockBanner({ knocks, onRespond }: KnockBannerProps): JSX.Element | null {
  // Cards on their way out after Let in, kept as they last looked.
  const [leaving, setLeaving] = useState<readonly KnockEntry[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  const respond = useCallback(
    (knock: KnockEntry, allow: boolean) => {
      onRespond(knock.knockId, allow);
      if (!allow || !knock.answers?.length) return;
      setLeaving((prev) => [...prev.filter((k) => k.knockId !== knock.knockId), knock]);
      const t = setTimeout(() => {
        timers.current.delete(knock.knockId);
        setLeaving((prev) => prev.filter((k) => k.knockId !== knock.knockId));
      }, LEAVE_MS);
      timers.current.set(knock.knockId, t);
    },
    [onRespond],
  );

  const live = knocks.filter((k) => !leaving.some((l) => l.knockId === k.knockId));
  if (live.length === 0 && leaving.length === 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: "var(--space-4)",
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        width: "min(92vw, 420px)",
        zIndex: 30,
      }}
    >
      {live.map((knock) => (
        <KnockCard key={knock.knockId} knock={knock} onRespond={respond} />
      ))}
      {leaving.map((knock) => (
        <KnockCard key={`out-${knock.knockId}`} knock={knock} leaving />
      ))}
    </div>
  );
}

function KnockCard({
  knock,
  leaving = false,
  onRespond,
}: {
  readonly knock: KnockEntry;
  readonly leaving?: boolean;
  readonly onRespond?: (knock: KnockEntry, allow: boolean) => void;
}): JSX.Element {
  const answers = knock.answers ?? [];
  const right = answers.filter((a) => a.right).length;
  const shown = answers.slice(-SHOWN).reverse();

  return (
    <div className={`knock-card${leaving ? " knock-card--leaving" : ""}`} aria-hidden={leaving || undefined}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)" }}>
        <span style={{ fontSize: 15, minWidth: 0 }}>
          {knock.name} is waiting
          {answers.length ? (
            <span className="knock-card__score">
              {right} of {answers.length} right
            </span>
          ) : null}
        </span>
        <div style={{ display: "flex", gap: "var(--space-2)", flexShrink: 0 }}>
          <button
            type="button"
            disabled={leaving}
            onClick={() => onRespond?.(knock, false)}
            style={{
              minHeight: "var(--tap-min)",
              padding: "0 var(--space-3)",
              borderRadius: "var(--radius-sm)",
              border: "1px solid rgba(255,255,255,0.3)",
              background: "transparent",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Not now
          </button>
          <button
            type="button"
            disabled={leaving}
            onClick={() => onRespond?.(knock, true)}
            style={{
              minHeight: "var(--tap-min)",
              padding: "0 var(--space-3)",
              borderRadius: "var(--radius-sm)",
              border: "none",
              background: "var(--accent)",
              color: "var(--accent-contrast)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Let in
          </button>
        </div>
      </div>
      {shown.length ? (
        <ul className="knock-card__stack" aria-label="Trivia they answered">
          {shown.map((a, i) => (
            <AnswerRow key={`${answers.length - i}`} answer={a} index={i} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function AnswerRow({ answer, index }: { readonly answer: KnockAnswer; readonly index: number }): JSX.Element {
  return (
    <li className="knock-card__answer" style={{ ["--i" as string]: index }}>
      <span className={`knock-card__mark knock-card__mark--${answer.right ? "right" : "wrong"}`} aria-label={answer.right ? "Right" : "Wrong"}>
        {answer.right ? "✓" : "✕"}
      </span>
      <span className="knock-card__q">{answer.q}</span>
    </li>
  );
}
